import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { FaArrowRight, FaPlay, FaYoutube } from 'react-icons/fa'
import { TbMicrophone2 } from 'react-icons/tb'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { coversFrom } from '../utils/coverUrl'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

function VideoPage({ items: episodes = [] }: { items?: any[] }) {
  // Newest episode first.
  const videos = useMemo(
    () =>
      episodes
        .slice()
        .sort((a, b) => (b.episode ?? 0) - (a.episode ?? 0))
        .map((ep) => ({ ...ep, youtubeUrl: ep.youtube || '', content: ep.description || '' })),
    [episodes],
  )

  useEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const offset = (headerEl?.offsetHeight ?? 0) + (stickyNavEl?.offsetHeight ?? 0)
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }
    measure()
    requestAnimationFrame(() => measure())
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  return (
    <div className="bg-brand-cream min-h-screen">
      {/* ── Hero ── */}
      <section className="bg-brand-navy text-white">
        <ContentColumn anchor="video-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-16 md:pb-24">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-heading text-caption uppercase tracking-[0.18em] text-white/65">
              <li><Link to="/" className="text-white/65 hover:text-white transition-colors">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-white" aria-current="page">Podcast</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 font-heading text-overline uppercase tracking-[0.16em] text-brand-red mb-5">
              <TbMicrophone2 className="text-sm" aria-hidden="true" />
              The Podcast · {videos.length} episode{videos.length === 1 ? '' : 's'}
            </span>
            <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold leading-[1.1] tracking-[-0.022em] text-white">
              Gazar <InlineHighlight color="red">Breakpoint</InlineHighlight>
            </h1>
            <p className="text-[18px] sm:text-[20px] leading-relaxed text-white/70 mt-6">
              Not a code tutorial channel. Gazar Breakpoint breaks down system design failures, architectural trade-offs, and the hard decisions that define a senior engineering career, real infrastructure mistakes and the reasoning behind resilient, high-scale systems.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="#episodes" className="group inline-flex items-center gap-2 bg-white text-brand-navy font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity">
                Browse episodes
                <FaArrowRight className="text-sm transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
              <a
                href="https://www.youtube.com/@gazarbreakpoint"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 bg-brand-red text-white font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity"
              >
                <FaYoutube className="text-lg" aria-hidden="true" />
                Subscribe on YouTube
              </a>
            </div>
          </div>
          {/* 16:9 — these are YouTube thumbnails, and cropping them to 4:3 cuts the
              episode titles in half. */}
            <HeroArtGrid images={coversFrom(videos)} aspect="16/9" />
          </div>
        </ContentColumn>
      </section>

      {/* ── Episodes ── */}
      <section id="episodes" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-heading text-h3 font-bold text-brand-navy">All episodes</h2>
          <a
            href="https://www.youtube.com/@gazarbreakpoint"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-brand-navy/60 hover:text-brand-red transition-colors"
          >
            On YouTube ↗
          </a>
        </div>

        {videos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {videos.map((video: any) => {
              const isNew =
                video.publishedAt &&
                new Date().getTime() - new Date(video.publishedAt).getTime() < 1000 * 60 * 60 * 24 * 7
              const date = video.publishedAt || video.createdAt
              return (
                <article key={video.id} className="group">
                  <Link
                    to={`/podcast/${video.slug}`}
                    className="flex flex-col h-full bg-white border border-brand-navy/12 rounded-xl overflow-hidden hover:border-brand-red/60 hover:shadow-[0_16px_40px_-18px_rgba(9,36,75,0.4)] transition-all duration-200"
                  >
                    <div className="relative w-full bg-brand-navy overflow-hidden" style={{ aspectRatio: '16/9' }}>
                      {video.image?.url && (
                        <>
                          <img
                            src={video.image.url}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-brand-navy/10 group-hover:bg-brand-navy/0 transition-colors duration-300" />
                        </>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand-red text-white shadow-xl">
                          <span className="absolute inset-0 -m-1.5 rounded-full bg-brand-red/30 animate-ping" aria-hidden="true" />
                          <FaPlay className="relative text-lg ml-0.5" aria-hidden="true" />
                        </span>
                      </div>
                      {isNew && (
                        <span className="absolute top-3 left-3 bg-brand-red text-white text-caption font-heading font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow">
                          New
                        </span>
                      )}
                      {video.episode != null && (
                        <span className="absolute top-3 right-3 bg-brand-navy/70 backdrop-blur-sm text-white text-caption font-accent font-semibold px-2 py-0.5 rounded">
                          Ep. {video.episode}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-5">
                      {date && (
                        <time className="text-caption text-brand-navy/50 font-heading uppercase tracking-wider mb-2">
                          {new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' })}
                        </time>
                      )}
                      <h3 className="font-heading text-body font-bold text-brand-navy leading-snug line-clamp-2 group-hover:text-brand-red transition-colors">
                        {video.title}
                      </h3>
                      {video.content && (
                        <p className="text-body-sm text-brand-navy/65 line-clamp-2 mt-2">
                          {video.content.replace(/\s+/g, ' ').slice(0, 130).trim()}
                          {video.content.length > 130 ? '…' : ''}
                        </p>
                      )}
                      <span className="mt-4 inline-flex items-center gap-1.5 text-body-sm font-heading font-semibold text-brand-navy group-hover:text-brand-red transition-colors">
                        Watch episode
                        <FaArrowRight className="text-[11px] transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                      </span>
                    </div>
                  </Link>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16 rounded-xl border border-brand-navy/12 bg-white">
            <p className="text-brand-navy/60 mb-2">No episodes available yet.</p>
            <a
              href="https://www.youtube.com/@gazarbreakpoint"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-navy font-semibold underline decoration-brand-navy/40 hover:text-brand-red"
            >
              Watch on YouTube
            </a>
          </div>
        )}
      </section>
    </div>
  )
}

export default VideoPage
